import { APP_STORAGE_NAMESPACE } from '../utils/storageNames'

/**
 * Clean up is a developer feature (docs/screenshot-cleanup/PLAN.md): it shows only for the current server-verified administrator who
 * also switched it on for this account on this device. The switch never grants anything without the verified administrator.
 */
const key = (owner: string) => `${APP_STORAGE_NAMESPACE}:dev-screenshot-cleanup:v1:${owner}`

export function readCleanupPreference(owner: string | null): boolean {
  if (!owner) return false
  try { return localStorage.getItem(key(owner)) === 'on' } catch { return false }
}

export function writeCleanupPreference(owner: string, enabled: boolean): void {
  if (enabled) localStorage.setItem(key(owner), 'on')
  else localStorage.removeItem(key(owner))
}

export const cleanupAvailable = (developerAuthorized: boolean, requested: boolean) => developerAuthorized && requested

/** What Unpack does along the way (Settings → Unpack), for this account on this device; everything is on until switched off. */
export interface UnpackPreferences {
  /** The cleaned photo is the note's picture in Browse. */
  cover: boolean
  /** The rest of a post is added below lines pasted from it. */
  addRest: boolean
  /** A dated booking shows in Quick Access from the day before to the day after. */
  quickAccess: boolean
  /** A booking offers a reminder the day before (set only on a tap). */
  remind: boolean
}
export const DEFAULT_UNPACK_PREFERENCES: UnpackPreferences = { cover: true, addRest: true, quickAccess: true, remind: true }
const preferencesKey = (owner: string) => `${APP_STORAGE_NAMESPACE}:unpack-preferences:v1:${owner}`

export function readUnpackPreferences(owner: string | null): UnpackPreferences {
  if (!owner) return DEFAULT_UNPACK_PREFERENCES
  try {
    const stored = JSON.parse(localStorage.getItem(preferencesKey(owner)) ?? '{}') as Partial<Record<keyof UnpackPreferences, unknown>>
    return Object.fromEntries(Object.entries(DEFAULT_UNPACK_PREFERENCES).map(([name, value]) => [name, typeof stored[name as keyof UnpackPreferences] === 'boolean' ? stored[name as keyof UnpackPreferences] : value])) as unknown as UnpackPreferences
  } catch { return DEFAULT_UNPACK_PREFERENCES }
}

export function writeUnpackPreferences(owner: string, preferences: UnpackPreferences): void {
  localStorage.setItem(preferencesKey(owner), JSON.stringify(preferences))
}
